import {Controller} from "@hotwired/stimulus";

const MODAL_ID = "avatar-modal";

// Global avatar modal controller.
// One modal in the admin layout, triggers anywhere pass context via data attributes.
export default class extends Controller {
  open(event) {
    const trigger = event.currentTarget;
    const email = trigger.dataset.avatarModalEmailParam || "";
    const initials = trigger.dataset.avatarModalInitialsParam || "";
    const emailHash = trigger.dataset.avatarModalEmailHashParam || "";

    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    // Store reference to trigger for updating after save/delete
    modal.avatarModalCurrentTrigger = trigger;

    const emailField = modal.querySelector("#avatar_email");
    if (emailField) emailField.value = email;

    modal.dataset.avatarModalCurrentEmailHash = emailHash;
    modal.dataset.avatarModalCurrentInitials = initials;
    modal.dataset.avatarModalCurrentEmail = email;

    // Remove existing preview img to force fresh fetch (same URL won't refetch on same element)
    const existingPreviewImg = modal.querySelector("#avatar-preview img");
    if (existingPreviewImg) existingPreviewImg.remove();

    // Fetch current avatar URL via the redirect endpoint
    // Add cache-buster to force fresh request (browsers may cache redirects in memory despite no-store)
    const baseUrl = emailHash ? modal.dataset.avatarUrlTemplate?.replace(":email_hash", emailHash) : "";
    const avatarUrl = baseUrl ? `${baseUrl}?cacheBustKey=${Date.now()}` : "";

    this.updatePreview(modal, {initials, avatarUrl});

    // Reset file field and error state
    const fileField = modal.querySelector("#avatar_file");
    if (fileField) fileField.value = "";

    const errorBox = modal.querySelector("#avatar-error");
    if (errorBox) errorBox.style.display = "none";

    this.showModal(modal);
  }

  close(event) {
    const modal = event.currentTarget.closest(".wg-modal-container");
    if (!modal) return;

    this.hideModal(modal);
  }

  async save(event) {
    const modal = event.currentTarget.closest(".wg-modal-container");
    if (!modal) return;

    const trigger = modal.avatarModalCurrentTrigger;
    const emailField = modal.querySelector("#avatar_email");
    const fileField = modal.querySelector("#avatar_file");
    const errorBox = modal.querySelector("#avatar-error");
    const saveButton = modal.querySelector("#avatar-save-button");
    const uploadUrl = modal.dataset.uploadUrl;

    if (!uploadUrl || !emailField || !fileField) return;

    const formData = new FormData();
    formData.append("avatar[email]", emailField.value);
    if (fileField.files[0]) formData.append("avatar[image]", fileField.files[0]);

    if (errorBox) {
      errorBox.style.display = "none";
      errorBox.textContent = "";
    }

    if (saveButton) saveButton.disabled = true;

    try {
      const csrfToken = document.querySelector("meta[name=\"csrf-token\"]")?.content;
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
        headers: csrfToken ? {"X-CSRF-Token": csrfToken} : {},
        credentials: "same-origin"
      });

      const data = await response.json();

      if (!response.ok) {
        const errors = data?.errors?.join(", ") || I18n.t("js.avatars.upload_error");
        if (errorBox) {
          errorBox.textContent = errors;
          errorBox.style.display = "block";
        }
        return;
      }

      // Update stored trigger with new avatar data
      if (trigger) {
        if (data?.email_hash) {
          trigger.dataset.avatarModalEmailHashParam = data.email_hash;
        }
        if (data?.url) {
          // Remove old img and create fresh one to ensure browser fetches new image
          const oldImg = trigger.querySelector("img");
          if (oldImg) oldImg.remove();

          const newImg = document.createElement("img");
          newImg.src = data.url;
          trigger.querySelector(".wg-avatar")?.appendChild(newImg);
        }
      }

      modal.dataset.avatarModalCurrentEmailHash = data.email_hash || "";

      const initials = trigger?.dataset.avatarModalInitialsParam || modal.dataset.avatarModalCurrentInitials || "";
      this.updatePreview(modal, {initials, avatarUrl: data?.url});

      this.hideModal(modal);
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = I18n.t("js.avatars.upload_error");
        errorBox.style.display = "block";
      }
    } finally {
      if (saveButton) saveButton.disabled = false;
    }
  }

  updatePreview(modal, {initials, avatarUrl}) {
    const preview = modal.querySelector("#avatar-preview");
    if (!preview) return;

    const initialsEl = preview.querySelector(".wg-avatar__initials");
    if (initialsEl) initialsEl.textContent = initials || "";

    let imageEl = preview.querySelector("img");
    if (!imageEl && avatarUrl) {
      imageEl = document.createElement("img");
      preview.querySelector(".wg-avatar")?.appendChild(imageEl);
    }

    if (imageEl) {
      if (avatarUrl) {
        imageEl.src = avatarUrl;
        imageEl.style.display = "block";
      } else {
        imageEl.src = "";
        imageEl.style.display = "none";
      }
    }
  }

  showModal(modal) {
    WGModal.open(`#${modal.id}`);
  }

  hideModal(modal) {
    WGModal.close(`#${modal.id}`);
  }

  openFileSelector(event) {
    const modal = event.currentTarget.closest(".wg-modal-container");
    if (!modal) return;

    const fileField = modal.querySelector("#avatar_file");
    if (fileField) fileField.click();
  }

  previewFile(event) {
    const modal = event.currentTarget.closest(".wg-modal-container");
    if (!modal) return;

    const file = event.currentTarget.files?.[0];
    if (!file) return;

    const initials = modal.dataset.avatarModalCurrentInitials || "";
    const objectUrl = URL.createObjectURL(file);
    this.updatePreview(modal, {initials, avatarUrl: objectUrl});
  }

  async delete(event) {
    const modal = event.currentTarget.closest(".wg-modal-container");
    if (!modal) return;

    const trigger = modal.avatarModalCurrentTrigger;
    const emailHash = modal.dataset.avatarModalCurrentEmailHash;
    const deleteUrlTemplate = modal.dataset.deleteUrlTemplate;
    const errorBox = modal.querySelector("#avatar-error");
    const deleteButton = modal.querySelector("#avatar-delete-button");

    if (!deleteUrlTemplate || !emailHash) {
      this.hideModal(modal);
      return;
    }

    if (errorBox) {
      errorBox.style.display = "none";
      errorBox.textContent = "";
    }

    if (deleteButton) deleteButton.disabled = true;

    try {
      const csrfToken = document.querySelector("meta[name=\"csrf-token\"]")?.content;
      const deleteUrl = deleteUrlTemplate.replace(":email_hash", emailHash);
      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: csrfToken ? {"X-CSRF-Token": csrfToken} : {},
        credentials: "same-origin"
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const errors = data?.errors?.join(", ") || I18n.t("js.avatars.delete_error");
        if (errorBox) {
          errorBox.textContent = errors;
          errorBox.style.display = "block";
        }
        return;
      }

      // Clear avatar image from stored trigger
      if (trigger) {
        const triggerImage = trigger.querySelector("img");
        if (triggerImage) triggerImage.remove();
      }

      const initials = trigger?.dataset.avatarModalInitialsParam || modal.dataset.avatarModalCurrentInitials || "";
      this.updatePreview(modal, {initials, avatarUrl: null});

      const fileField = modal.querySelector("#avatar_file");
      if (fileField) fileField.value = "";

      this.hideModal(modal);
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = I18n.t("js.avatars.delete_error");
        errorBox.style.display = "block";
      }
    } finally {
      if (deleteButton) deleteButton.disabled = false;
    }
  }
}
